import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertCircle, CheckCircle2, Palette, RefreshCw } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  useProviderBrandingSettings,
  useUpdateProviderBrandingSettings,
} from '@/hooks/provider/useProviderBrandingSettings';
import {
  normalizeBrandingField,
  ProviderBrandingRequestError,
} from '@/services/provider/branding.service';
import { resolveBrandLogo } from '@/lib/branding';

type FormState = {
  isActive: boolean;
  logoUrl: string;
  logoLightUrl: string;
  logoDarkUrl: string;
  primaryColor: string;
  accentColor: string;
};

type FormErrors = Partial<Record<keyof FormState, string>>;

const HEX_COLOR_REGEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const MAX_LOGO_LENGTH = 2048;

function resolvePreviewColor(value: string, fallback: string): string {
  const normalized = normalizeBrandingField(value);
  if (!normalized || !HEX_COLOR_REGEX.test(normalized)) {
    return fallback;
  }
  return normalized;
}

export function ProviderBrandingSettingsCard() {
  const { t } = useTranslation();
  const settingsQuery = useProviderBrandingSettings();
  const updateMutation = useUpdateProviderBrandingSettings();

  const [formState, setFormState] = useState<FormState>({
    isActive: false,
    logoUrl: '',
    logoLightUrl: '',
    logoDarkUrl: '',
    primaryColor: '',
    accentColor: '',
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  useEffect(() => {
    if (settingsQuery.data) {
      setFormState({
        isActive: settingsQuery.data.isActive ?? false,
        logoUrl: settingsQuery.data.logoUrl ?? '',
        logoLightUrl: settingsQuery.data.logoLightUrl ?? '',
        logoDarkUrl: settingsQuery.data.logoDarkUrl ?? '',
        primaryColor: settingsQuery.data.primaryColor ?? '',
        accentColor: settingsQuery.data.accentColor ?? '',
      });
      setFormErrors({});
    }
  }, [settingsQuery.data]);

  const isSaving = updateMutation.isPending;

  const preview = useMemo(() => {
    const primary = resolvePreviewColor(formState.primaryColor, 'hsl(var(--primary))');
    const accent = resolvePreviewColor(formState.accentColor, 'hsl(var(--secondary))');
    const logoDefault = normalizeBrandingField(formState.logoUrl) ?? undefined;
    const logoLight = normalizeBrandingField(formState.logoLightUrl) ?? undefined;
    const logoDark = normalizeBrandingField(formState.logoDarkUrl) ?? undefined;
    return {
      primary,
      accent,
      logoDefault,
      logoLight,
      logoDark,
      lightLogo: resolveBrandLogo(
        {
          logo: logoDefault,
          logoLight,
          logoDark,
        },
        'light',
      ),
      darkLogo: resolveBrandLogo(
        {
          logo: logoDefault,
          logoLight,
          logoDark,
        },
        'dark',
      ),
    };
  }, [
    formState.accentColor,
    formState.logoUrl,
    formState.logoDarkUrl,
    formState.logoLightUrl,
    formState.primaryColor,
  ]);

  const validate = (state: FormState): FormErrors => {
    const next: FormErrors = {};
    const logo = state.logoUrl.trim();
    const logoLight = state.logoLightUrl.trim();
    const logoDark = state.logoDarkUrl.trim();
    const primary = state.primaryColor.trim();
    const accent = state.accentColor.trim();

    if (logo && logo.length > MAX_LOGO_LENGTH) {
      next.logoUrl = t(
        'settings.branding.validation.logoTooLong',
        'Logo URL must be 2048 characters or fewer.',
      );
    }
    if (logoLight && logoLight.length > MAX_LOGO_LENGTH) {
      next.logoLightUrl = t(
        'settings.branding.validation.logoTooLong',
        'Logo URL must be 2048 characters or fewer.',
      );
    }
    if (logoDark && logoDark.length > MAX_LOGO_LENGTH) {
      next.logoDarkUrl = t(
        'settings.branding.validation.logoTooLong',
        'Logo URL must be 2048 characters or fewer.',
      );
    }

    if (primary && !HEX_COLOR_REGEX.test(primary)) {
      next.primaryColor = t(
        'settings.branding.validation.colorInvalid',
        'Enter a valid hex color like #0f172a.',
      );
    }

    if (accent && !HEX_COLOR_REGEX.test(accent)) {
      next.accentColor = t(
        'settings.branding.validation.colorInvalid',
        'Enter a valid hex color like #0f172a.',
      );
    }

    return next;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const nextErrors = validate(formState);
    setFormErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    const payload = {
      isActive: formState.isActive,
      logoUrl: normalizeBrandingField(formState.logoUrl),
      logoLightUrl: normalizeBrandingField(formState.logoLightUrl),
      logoDarkUrl: normalizeBrandingField(formState.logoDarkUrl),
      primaryColor: normalizeBrandingField(formState.primaryColor),
      accentColor: normalizeBrandingField(formState.accentColor),
    };

    try {
      const updated = await updateMutation.mutateAsync(payload);
      setFormState({
        isActive: updated.isActive ?? false,
        logoUrl: updated.logoUrl ?? '',
        logoLightUrl: updated.logoLightUrl ?? '',
        logoDarkUrl: updated.logoDarkUrl ?? '',
        primaryColor: updated.primaryColor ?? '',
        accentColor: updated.accentColor ?? '',
      });
      setFormErrors({});
      toast.success(t('settings.branding.messages.saveSuccess', 'Branding settings saved.'));
    } catch (error) {
      if (error instanceof ProviderBrandingRequestError && error.errors) {
        const next: FormErrors = {};
        if (error.errors.is_active?.[0]) {
          next.isActive = error.errors.is_active[0];
        }
        if (error.errors.logo_url?.[0]) {
          next.logoUrl = error.errors.logo_url[0];
        }
        if (error.errors.logo_light_url?.[0]) {
          next.logoLightUrl = error.errors.logo_light_url[0];
        }
        if (error.errors.logo_dark_url?.[0]) {
          next.logoDarkUrl = error.errors.logo_dark_url[0];
        }
        if (error.errors.primary_color?.[0]) {
          next.primaryColor = error.errors.primary_color[0];
        }
        if (error.errors.accent_color?.[0]) {
          next.accentColor = error.errors.accent_color[0];
        }
        setFormErrors(next);
      }
      const message =
        error instanceof Error && error.message
          ? error.message
          : t('settings.branding.messages.saveError', 'Unable to save branding settings.');
      toast.error(message);
    }
  };

  if (settingsQuery.isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5 text-muted-foreground" />
            {t('settings.branding.title', 'Branding')}
          </CardTitle>
          <CardDescription>
            {t(
              'settings.branding.description',
              'Customize your customer-facing brand experience.',
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-32" />
        </CardContent>
      </Card>
    );
  }

  if (settingsQuery.isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            {t('settings.branding.title', 'Branding')}
          </CardTitle>
          <CardDescription>
            {t(
              'settings.branding.description',
              'Customize your customer-facing brand experience.',
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertTitle>
              {t('settings.branding.messages.loadError', 'Unable to load branding settings.')}
            </AlertTitle>
            <AlertDescription className="mt-2 flex items-center gap-3">
              <span>{settingsQuery.error.message}</span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => settingsQuery.refetch()}
                className="gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                {t('common.retry', 'Retry')}
              </Button>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const providerName =
    settingsQuery.data?.providerName ??
    t('settings.branding.preview.defaultName', 'Provider');
  const providerUrl = settingsQuery.data?.providerUrl ?? '';
  const providerInitial = providerName.trim().charAt(0).toUpperCase() || 'P';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Palette className="h-5 w-5 text-muted-foreground" />
          {t('settings.branding.title', 'Branding')}
        </CardTitle>
        <CardDescription>
          {t(
            'settings.branding.description',
            'Customize your customer-facing brand experience.',
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="space-y-6" onSubmit={handleSubmit}>
          <div className="flex flex-wrap items-start justify-between gap-4 rounded-lg border bg-muted/30 p-4">
            <div className="space-y-1">
              <p className="text-sm font-medium">
                {t('settings.branding.fields.active', 'Enable branding')}
              </p>
              <p className="text-xs text-muted-foreground">
                {t(
                  'settings.branding.helpers.active',
                  'When enabled, your logo and colors appear on customer-facing pages.',
                )}
              </p>
              {formErrors.isActive ? (
                <p className="text-sm text-destructive">{formErrors.isActive}</p>
              ) : null}
            </div>
            <Switch
              checked={formState.isActive}
              onCheckedChange={(checked) =>
                setFormState((prev) => ({ ...prev, isActive: checked }))
              }
              disabled={isSaving}
            />
          </div>

          <div className="rounded-lg border bg-muted/20 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium">
                  {t('settings.branding.preview.title', 'Preview')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t(
                    'settings.branding.preview.description',
                    'Preview how your branding will appear.',
                  )}
                </p>
              </div>
              <Badge variant="secondary" className="text-xs">
                {formState.isActive
                  ? t('common.active', 'Active')
                  : t('common.inactive', 'Inactive')}
              </Badge>
            </div>
            <div className="mt-4 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="space-y-1">
                  <p className="text-sm font-semibold">{providerName}</p>
                  {providerUrl ? (
                    <a
                      href={providerUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-muted-foreground underline"
                    >
                      {providerUrl}
                    </a>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      {t(
                        'settings.branding.preview.urlPlaceholder',
                        'Provider URL unavailable.',
                      )}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className="rounded-full px-2 py-1 text-xs font-medium text-white"
                    style={{ backgroundColor: preview.primary }}
                  >
                    {t('settings.branding.preview.primary', 'Primary')}
                  </span>
                  <span
                    className="rounded-full px-2 py-1 text-xs font-medium text-white"
                    style={{ backgroundColor: preview.accent }}
                  >
                    {t('settings.branding.preview.accent', 'Accent')}
                  </span>
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-md border bg-background p-3">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    {t('settings.branding.preview.lightLabel', 'Light mode')}
                  </p>
                  <div className="mt-2 flex items-center gap-3">
                    {preview.lightLogo ? (
                      <img
                        src={preview.lightLogo}
                        alt={t('settings.branding.preview.logoAlt', '{{provider}} logo', {
                          provider: providerName,
                        })}
                        className="h-8 w-auto max-w-[140px] object-contain"
                      />
                    ) : (
                      <div className="flex h-8 w-8 items-center justify-center rounded-md border bg-background text-xs font-semibold">
                        {providerInitial}
                      </div>
                    )}
                    <span className="text-sm font-medium">{providerName}</span>
                  </div>
                </div>
                <div className="rounded-md border bg-slate-950/90 p-3 text-white">
                  <p className="text-[11px] uppercase tracking-wide text-white/70">
                    {t('settings.branding.preview.darkLabel', 'Dark mode')}
                  </p>
                  <div className="mt-2 flex items-center gap-3">
                    {preview.darkLogo ? (
                      <img
                        src={preview.darkLogo}
                        alt={t('settings.branding.preview.logoAlt', '{{provider}} logo', {
                          provider: providerName,
                        })}
                        className="h-8 w-auto max-w-[140px] object-contain"
                      />
                    ) : (
                      <div className="flex h-8 w-8 items-center justify-center rounded-md border border-white/20 bg-white/10 text-xs font-semibold text-white">
                        {providerInitial}
                      </div>
                    )}
                    <span className="text-sm font-medium">{providerName}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {t('settings.branding.fields.logoDarkUrl', 'Dark logo URL')}
                </label>
                <Input
                  value={formState.logoDarkUrl}
                  placeholder={t(
                    'settings.branding.placeholders.logoDarkUrl',
                    'https://cdn.example.com/brand/logo-dark.png',
                  )}
                  onChange={(event) =>
                    setFormState((prev) => ({ ...prev, logoDarkUrl: event.target.value }))
                  }
                  disabled={isSaving}
                />
                <p className="text-xs text-muted-foreground">
                  {t(
                    'settings.branding.helpers.logoDarkUrl',
                    'Shown on light mode backgrounds.',
                  )}
                </p>
                {formErrors.logoDarkUrl ? (
                  <p className="text-sm text-destructive">{formErrors.logoDarkUrl}</p>
                ) : null}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {t('settings.branding.fields.logoLightUrl', 'Light logo URL')}
                </label>
                <Input
                  value={formState.logoLightUrl}
                  placeholder={t(
                    'settings.branding.placeholders.logoLightUrl',
                    'https://cdn.example.com/brand/logo-light.png',
                  )}
                  onChange={(event) =>
                    setFormState((prev) => ({ ...prev, logoLightUrl: event.target.value }))
                  }
                  disabled={isSaving}
                />
                <p className="text-xs text-muted-foreground">
                  {t(
                    'settings.branding.helpers.logoLightUrl',
                    'Shown on dark mode backgrounds.',
                  )}
                </p>
                {formErrors.logoLightUrl ? (
                  <p className="text-sm text-destructive">{formErrors.logoLightUrl}</p>
                ) : null}
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('settings.branding.fields.logoUrl', 'Fallback logo URL')}
              </label>
              <Input
                value={formState.logoUrl}
                placeholder={t(
                  'settings.branding.placeholders.logoUrl',
                  'https://cdn.example.com/brand/logo.png',
                )}
                onChange={(event) =>
                  setFormState((prev) => ({ ...prev, logoUrl: event.target.value }))
                }
                disabled={isSaving}
              />
              <p className="text-xs text-muted-foreground">
                {t(
                  'settings.branding.helpers.logoUrl',
                  'Used when a theme-specific logo is not provided.',
                )}
              </p>
              {formErrors.logoUrl ? (
                <p className="text-sm text-destructive">{formErrors.logoUrl}</p>
              ) : null}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('settings.branding.fields.primaryColor', 'Primary color')}
              </label>
              <div className="flex items-center gap-3">
                <div
                  className="h-10 w-10 rounded-md border"
                  style={{
                    backgroundColor: resolvePreviewColor(
                      formState.primaryColor,
                      'transparent',
                    ),
                  }}
                />
                <Input
                  value={formState.primaryColor}
                  placeholder={t('settings.branding.placeholders.primaryColor', '#0f172a')}
                  onChange={(event) =>
                    setFormState((prev) => ({ ...prev, primaryColor: event.target.value }))
                  }
                  disabled={isSaving}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {t(
                  'settings.branding.helpers.primaryColor',
                  'Hex color used for primary actions and highlights.',
                )}
              </p>
              {formErrors.primaryColor ? (
                <p className="text-sm text-destructive">{formErrors.primaryColor}</p>
              ) : null}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('settings.branding.fields.accentColor', 'Accent color')}
              </label>
              <div className="flex items-center gap-3">
                <div
                  className="h-10 w-10 rounded-md border"
                  style={{
                    backgroundColor: resolvePreviewColor(
                      formState.accentColor,
                      'transparent',
                    ),
                  }}
                />
                <Input
                  value={formState.accentColor}
                  placeholder={t('settings.branding.placeholders.accentColor', '#22c55e')}
                  onChange={(event) =>
                    setFormState((prev) => ({ ...prev, accentColor: event.target.value }))
                  }
                  disabled={isSaving}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {t(
                  'settings.branding.helpers.accentColor',
                  'Hex color used for secondary actions and accents.',
                )}
              </p>
              {formErrors.accentColor ? (
                <p className="text-sm text-destructive">{formErrors.accentColor}</p>
              ) : null}
            </div>
          </div>

          <div className="flex items-center justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setFormState((prev) => ({
                  ...prev,
                  logoUrl: '',
                  logoLightUrl: '',
                  logoDarkUrl: '',
                  primaryColor: '',
                  accentColor: '',
                }));
                setFormErrors({});
              }}
              disabled={isSaving}
            >
              {t('common.clear', 'Clear')}
            </Button>
            <Button type="submit" disabled={isSaving} className="gap-2">
              {isSaving && <CheckCircle2 className="h-4 w-4 animate-spin" />}
              {t('settings.branding.actions.save', 'Save branding')}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
